import { Button, Grid, Stack, SvgIcon, Typography } from '@mui/material';
import { FunctionComponent, useState } from 'react';
import { DataColumn } from '../../../types';
import { Add } from '@mui/icons-material';
import AddDataColumnDialog from '../add-data-column/AddDataColumnDialog';
import DataColumnDraggable from './DataColumnDraggable';
import { v4 as uuid } from 'uuid';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';

interface IDataColumnListProps {
  dataColumns: DataColumn[];
  onChange: (assetProperties: DataColumn[]) => void;
  currentSelectedAggregationInterval: string;
}

const DataColumnList: FunctionComponent<IDataColumnListProps> = ({
  dataColumns,
  onChange,
  currentSelectedAggregationInterval,
}) => {
  const [openAssetPropertyColumn, setOpenAssetPropertyColumn] = useState(false);
  const [dataColumn, setDataColumn] = useState<DataColumn | null>(null);
  const sensors = useSensors(useSensor(PointerSensor));
  const [isEdit, setIsEdit] = useState<boolean>(false);

  return (
    <Stack gap="8px">
      <Typography variant="body1">Columns</Typography>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          const { active, over } = event;
          if (active.id !== over?.id) {
            const oldIndex = dataColumns.findIndex((p) => p.id === active.id);
            const newIndex = dataColumns.findIndex((p) => p.id === over?.id);
            const newItems = arrayMove(dataColumns, oldIndex, newIndex);
            const orderedItems = newItems.map((item, index) => ({
              ...item,
              order: index,
            }));
            onChange(orderedItems);
          }
        }}
      >
        <SortableContext items={dataColumns.map((p) => p.id)} strategy={rectSortingStrategy}>
          <Grid container spacing={2}>
            {dataColumns.map((property, index: number) => (
              <Grid item xs={12} sm={6} md={6} lg={4} xl={3} key={property.id}>
                <DataColumnDraggable
                  item={property}
                  onEdit={(data) => {
                    setDataColumn(data);
                    setOpenAssetPropertyColumn(true);
                    setIsEdit(true);
                  }}
                  onRemove={(data) => {
                    const updated = dataColumns
                      .filter((asset) => asset.id !== data.id)
                      .map((item, index) => ({
                        ...item,
                        order: index,
                      }));
                    onChange(updated);
                  }}
                  key={property.id}
                  index={index}
                />
              </Grid>
            ))}
          </Grid>
        </SortableContext>
      </DndContext>

      <Stack flexDirection="row" justifyContent="flex-end">
        <Button
          onClick={() => {
            setOpenAssetPropertyColumn(true);
          }}
          variant="text"
          color="primary"
          data-sm="add-columnt-btn"
        >
          <Stack flexDirection="row" gap="12px">
            <SvgIcon component={Add} />
            <Typography variant="body1">Add column</Typography>
          </Stack>
        </Button>
      </Stack>

      <AddDataColumnDialog
        dataColumnsList={dataColumns}
        onClose={() => {
          setDataColumn(null);
          setOpenAssetPropertyColumn(false);
        }}
        onCancel={() => {
          setDataColumn(null);
          setOpenAssetPropertyColumn(false);
        }}
        dataColumn={dataColumn}
        onSubmit={(data) => {
          setDataColumn(null);
          let newArray: DataColumn[];
          const hasAggregation = data?.aggregation !== undefined;
          const aggregationValue =
            hasAggregation && !data?.aggregation?.includes('undefined')
              ? {
                  value: (data.aggregation as string).split('_')[0],
                  label: (data.aggregation as string).split('_')[1],
                }
              : null;

          const tags = data.tags ?? [];

          if (isEdit && dataColumn) {
            const updatedColumn: DataColumn = {
              ...dataColumn,
              tag: tags[0] ?? dataColumn.tag,
              aggregation: aggregationValue,
              columnStatistics: data.columnAggregation,
              unit: data.unit ?? '',
            };
            const additionalColumns: DataColumn[] = tags?.slice(1).map((tag) => ({
              id: uuid(),
              tag,
              aggregation: aggregationValue,
              columnStatistics: data.columnAggregation,
              order: 0,
              unit: data.unit ?? '',
            }));
            const editedIndex = dataColumns?.findIndex((col) => col.id === dataColumn.id);
            newArray = [
              ...dataColumns?.slice(0, editedIndex).map((col) => col),
              updatedColumn,
              ...additionalColumns,
              ...dataColumns?.slice(editedIndex + 1),
            ];
          } else {
            const newColumns: DataColumn[] = tags.map((tag) => ({
              id: uuid(),
              tag,
              aggregation: aggregationValue,
              columnStatistics: data.columnAggregation,
              order: 0,
              unit: data.unit ?? '',
            }));
            newArray = [...dataColumns, ...newColumns];
          }

          newArray = newArray.map((item, index) => ({
            ...item,
            order: index,
          }));

          onChange(newArray);
          setOpenAssetPropertyColumn(false);
        }}
        open={openAssetPropertyColumn}
        currentSelectedAggregationInterval={currentSelectedAggregationInterval}
      />
    </Stack>
  );
};

export default DataColumnList;
